/**
 * How a venue's `kind` becomes a heading in the sidebar tree.
 *
 * Deliberately its own small copy rather than an import from
 * `app/src/screens/Venues.tsx`, which defines the same shape for the mobile
 * Venues tab (`GROUPS`/`groupOf`). That file is a React component with
 * hooks; pulling it in here risks the same dual-React problem `useSession.ts`
 * avoids for auth. The grouping itself is a handful of stable facts about
 * the schema (`venues_kind_check`), not logic worth the coupling — if the
 * two ever need to drift, that is fine, because the question they are
 * answering is not quite the same one: Venues.tsx groups *places with
 * something on this week*, this groups *every venue there is to edit*.
 */

/** `venues_kind_check` in Postgres — the only values a row can hold. */
export const KINDS = [
  'building', 'hall', 'cinema', 'screen', 'museum', 'gallery',
  'theatre', 'stage', 'club', 'market', 'outdoor', 'other',
  'comedy', 'church', 'dance', 'project_space', 'kunstverein', 'collection',
  'library', 'bookshop', 'synagogue',
  'bar',
] as const

export const GROUPS = [
  { id: 'stage', label: 'Concert halls and theatres' },
  { id: 'comedy', label: 'Comedy' },
  { id: 'cinema', label: 'Cinemas' },
  { id: 'museum', label: 'Museums' },
  { id: 'gallery', label: 'Galleries' },
  { id: 'market', label: 'Markets' },
  { id: 'other', label: 'Everywhere else' },
] as const

/** Six kinds were added on 22 September and every one of them has to be
 *  routed here, because an unrouted kind falls through to 'other' and a venue
 *  quietly leaves the heading it belongs under. A church and a dance house
 *  are places you sit down in the evening, so they go with the stages; a
 *  Kunstverein and a private collection are curated and not selling, so they
 *  go with the museums; a project space is still a gallery. */
export const groupOf = (kind: string | null): string => {
  if (kind === 'building' || kind === 'theatre' || kind === 'hall' || kind === 'stage'
    || kind === 'church' || kind === 'dance') return 'stage'
  if (kind === 'kunstverein' || kind === 'collection') return 'museum'
  if (kind === 'project_space') return 'gallery'
  if (kind === 'cinema' || kind === 'museum' || kind === 'gallery' || kind === 'market'
    || kind === 'comedy') return kind
  return 'other'
}
